import {
    ArrowLeftIcon,
    ArrowUturnLeftIcon,
    CheckCircleIcon,
    EnvelopeIcon,
    EyeIcon,
    EyeSlashIcon,
    PhoneIcon,
} from '@heroicons/react/24/outline';

import IconButton from '../../components/IconButton';
import Text from '../../components/Text';
import { t } from '../../i18n/langue';

/**
 * L'en-tête du fil — extrait de `FilConversation` pour lui garder une taille
 * lisible à mesure que les actions s'y accumulent (#16, #30, #85, puis l'appel et
 * le marquage non lu). Purement présentationnel : tous les gestes remontent par
 * callback, `FilConversation` reste seul à savoir CE QUE chacun déclenche.
 */
interface EnTeteFilProps {
    titre: string;
    estGroupe: boolean;
    nomEtablissement?: string | null;
    onRetour: () => void;
    /** `null` sans numéro exploitable : pas de bouton plutôt qu'un bouton mort. */
    lienAppel: string | null;
    /** `null` tant que le dossier `closed` de l'établissement n'est pas connu. */
    idDossierFerme: number | null;
    fermee: boolean;
    fermetureEnCours: boolean;
    onBasculerFermeture: () => void;
    marquageNonLuEnCours: boolean;
    onMarquerNonLu: () => void;
    masque: boolean;
    onBasculerMasque: () => void;
}

export default function EnTeteFil({
    titre,
    estGroupe,
    nomEtablissement,
    onRetour,
    lienAppel,
    idDossierFerme,
    fermee,
    fermetureEnCours,
    onBasculerFermeture,
    marquageNonLuEnCours,
    onMarquerNonLu,
    masque,
    onBasculerMasque,
}: EnTeteFilProps) {
    return (
        <div className="flex items-center gap-2 border-b border-border-color-light bg-white px-3 py-2">
            <IconButton libelle={t('messagerie.fil.retourListe')} onPress={onRetour}>
                <ArrowLeftIcon className="h-6 w-6 text-text-color" />
            </IconButton>

            <div className="min-w-0 flex-1">
                <Text variante="sousTitre" as="h1" className="truncate">
                    {titre}
                </Text>
                {/* #25 : la conversation porte son hôtel sous sélection de groupe.
                    Arriver ici par notification, c'est arriver sans en-tête de liste. */}
                {estGroupe && nomEtablissement && (
                    <Text variante="petit" ton="marque" className="truncate">
                        {nomEtablissement}
                    </Text>
                )}
            </div>

            {/* Appeler en un tap — absent sans numéro exploitable : rien à proposer
                plutôt qu'un bouton qui ne mène nulle part. */}
            {lienAppel && (
                <IconButton
                    libelle={t('messagerie.fil.appeler', { nom: titre })}
                    onPress={() => {
                        window.location.href = lienAppel;
                    }}
                >
                    <PhoneIcon className="h-6 w-6 text-text-color" />
                </IconButton>
            )}

            {/*
             * Fermer vit ICI, dans le fil ouvert, jamais dans la liste (#30) :
             * c'est un jugement humain — « c'est réglé » — et on le porte
             * APRÈS avoir lu. Un bouton sur chaque ligne inviterait à fermer
             * sans lire, et le tri en rafale reste un travail de bureau.
             */}
            {idDossierFerme !== null && (
                <IconButton
                    libelle={fermee ? t('messagerie.fil.rouvrir') : t('messagerie.fil.fermer')}
                    onPress={onBasculerFermeture}
                    disabled={fermetureEnCours}
                >
                    {fermee ? (
                        <ArrowUturnLeftIcon className="h-6 w-6 text-main-color" />
                    ) : (
                        <CheckCircleIcon className="h-6 w-6 text-text-color" />
                    )}
                </IconButton>
            )}

            {/* Remettre en non lu — #86 : pour se la repasser à soi-même, ou à
                l'équipe. `disabled` porte la garde anti double-tap : le geste part
                une seule fois même sur deux taps rapprochés. */}
            <IconButton
                libelle={t('messagerie.fil.marquerNonLu')}
                onPress={onMarquerNonLu}
                disabled={marquageNonLuEnCours}
            >
                <EnvelopeIcon className="h-6 w-6 text-text-color" />
            </IconButton>

            <IconButton
                libelle={masque ? t('messagerie.fil.afficherAutomatiques') : t('messagerie.fil.masquerAutomatiques')}
                onPress={onBasculerMasque}
            >
                {masque ? (
                    <EyeSlashIcon className="h-6 w-6 text-main-color" />
                ) : (
                    <EyeIcon className="h-6 w-6 text-text-color" />
                )}
            </IconButton>
        </div>
    );
}
